// src/components/dashboard/CryptoWalletsInfoModal.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';

interface CryptoWalletsInfoModalProps {
  onClose: () => void;
}

export default function CryptoWalletsInfoModal({ onClose }: CryptoWalletsInfoModalProps) {
  const { t } = useTranslation();

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4'>
      <div className='bg-white rounded-[10px] w-full max-w-[900px] max-h-[90vh] overflow-y-auto'>
        {/* Header */}
        <div className='sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center rounded-t-[10px]'>
          <h2 className='text-2xl font-bold text-gray-800'>
            ℹ️ {t('wallets.infoModal.title')}
          </h2>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 text-3xl leading-none'
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className='p-6 space-y-6'>
          {/* Web3 Wallets Section */}
          <section>
            <h3 className='text-xl font-semibold text-gray-800 mb-3 flex items-center'>
              🔐 {t('wallets.infoModal.web3Title')}
            </h3>
            <p className='text-gray-700 mb-2'>{t('wallets.infoModal.web3Description')}</p>
            <p className='text-sm bg-blue-50 border-l-4 border-blue-400 p-3 text-blue-800'>
              <strong>{t('wallets.infoModal.web3Important')}</strong>
            </p>

            <div className='mt-4 bg-gray-50 p-4 rounded-lg'>
              <p className='font-semibold text-gray-800 mb-2'>{t('wallets.infoModal.optionsTitle')}</p>
              <ul className='space-y-2 text-sm text-gray-700'>
                <li className='flex items-start'>
                  <span className='text-green-500 mr-2'>✅</span>
                  <span>{t('wallets.infoModal.createWallet')}</span>
                </li>
                <li className='flex items-start'>
                  <span className='text-blue-500 mr-2'>📥</span>
                  <span>{t('wallets.infoModal.importWallet')}</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Supported Networks Section */}
          <section>
            <h3 className='text-xl font-semibold text-gray-800 mb-3 flex items-center'>
              🌐 {t('wallets.infoModal.networksTitle')}
            </h3>
            <p className='text-gray-700 mb-4'>{t('wallets.infoModal.networksDescription')}</p>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {/* Bitcoin */}
              <div className='border border-gray-200 rounded-lg p-4 bg-orange-50'>
                <h4 className='font-bold text-orange-600 mb-2'>1. {t('wallets.infoModal.bitcoin')}</h4>
                <ul className='text-sm space-y-1 text-gray-700'>
                  <li>• {t('wallets.infoModal.bitcoinNative')}</li>
                  <li>• {t('wallets.infoModal.bitcoinTokens')}</li>
                  <li>• {t('wallets.infoModal.bitcoinUse')}</li>
                </ul>
              </div>

              {/* Ethereum */}
              <div className='border border-gray-200 rounded-lg p-4 bg-purple-50'>
                <h4 className='font-bold text-purple-600 mb-2'>2. {t('wallets.infoModal.ethereum')}</h4>
                <ul className='text-sm space-y-1 text-gray-700'>
                  <li>• {t('wallets.infoModal.ethereumNative')}</li>
                  <li>• {t('wallets.infoModal.ethereumTokens')}</li>
                  <li className='text-xs italic'>✔️ {t('wallets.infoModal.ethereumAddress')}</li>
                </ul>
              </div>

              {/* BNB */}
              <div className='border border-gray-200 rounded-lg p-4 bg-yellow-50'>
                <h4 className='font-bold text-yellow-600 mb-2'>3. {t('wallets.infoModal.bnb')}</h4>
                <ul className='text-sm space-y-1 text-gray-700'>
                  <li>• {t('wallets.infoModal.bnbNative')}</li>
                  <li>• {t('wallets.infoModal.bnbTokens')}</li>
                  <li className='text-xs italic'>✔️ {t('wallets.infoModal.bnbAddress')}</li>
                </ul>
              </div>

              {/* TRON */}
              <div className='border border-gray-200 rounded-lg p-4 bg-red-50'>
                <h4 className='font-bold text-red-600 mb-2'>4. {t('wallets.infoModal.tron')}</h4>
                <ul className='text-sm space-y-1 text-gray-700'>
                  <li>• {t('wallets.infoModal.tronNative')}</li>
                  <li>• {t('wallets.infoModal.tronTokens')}</li>
                  <li className='text-xs italic'>✔️ {t('wallets.infoModal.tronAddress')}</li>
                </ul>
              </div>

              {/* Solana */}
              <div className='border border-gray-200 rounded-lg p-4 bg-green-50 md:col-span-2'>
                <h4 className='font-bold text-green-600 mb-2'>5. {t('wallets.infoModal.solana')}</h4>
                <ul className='text-sm space-y-1 text-gray-700'>
                  <li>• {t('wallets.infoModal.solanaNative')}</li>
                  <li>• {t('wallets.infoModal.solanaTokens')}</li>
                  <li className='text-xs italic'>✔️ {t('wallets.infoModal.solanaAddress')}</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Shared Address Section */}
          <section>
            <h3 className='text-xl font-semibold text-gray-800 mb-3 flex items-center'>
              🔄 {t('wallets.infoModal.sharedAddressTitle')}
            </h3>
            <p className='text-gray-700 mb-3'>{t('wallets.infoModal.sharedAddressDescription')}</p>
            <ul className='space-y-2 text-sm text-gray-700 bg-gray-50 p-4 rounded-lg'>
              <li>• {t('wallets.infoModal.sharedAddressEth')}</li>
              <li>• {t('wallets.infoModal.sharedAddressTron')}</li>
              <li>• {t('wallets.infoModal.sharedAddressBnb')}</li>
            </ul>
            <p className='text-sm text-gray-600 mt-2 italic'>{t('wallets.infoModal.sharedAddressWhy')}</p>
          </section>

          {/* Gas Fees Section */}
          <section>
            <h3 className='text-xl font-semibold text-gray-800 mb-3 flex items-center'>
              ⛽ {t('wallets.infoModal.gasFeesTitle')}
            </h3>
            <p className='text-gray-700 mb-3'>{t('wallets.infoModal.gasFeesDescription')}</p>

            <div className='bg-blue-50 border-l-4 border-blue-400 p-4 mb-3'>
              <p className='font-semibold text-blue-800 mb-2'>{t('wallets.infoModal.gasFeesExamples')}</p>
              <ul className='space-y-1 text-sm text-blue-900'>
                <li>• {t('wallets.infoModal.gasFeesEth')}</li>
                <li>• {t('wallets.infoModal.gasFeesTron')}</li>
                <li>• {t('wallets.infoModal.gasFeesBnb')}</li>
                <li>• {t('wallets.infoModal.gasFeesSol')}</li>
              </ul>
            </div>

            <div className='bg-green-50 p-4 rounded-lg'>
              <p className='text-sm font-semibold text-green-800 mb-2'>💡 {t('wallets.infoModal.gasFeesTip')}</p>
              <p className='text-sm font-semibold text-gray-800 mb-2'>{t('wallets.infoModal.gasFeesRecommended')}</p>
              <div className='grid grid-cols-2 gap-2 text-xs text-gray-700'>
                <span>• {t('wallets.infoModal.gasFeesEthAmount')}</span>
                <span>• {t('wallets.infoModal.gasFeesBnbAmount')}</span>
                <span>• {t('wallets.infoModal.gasFeesTronAmount')}</span>
                <span>• {t('wallets.infoModal.gasFeesSolAmount')}</span>
              </div>
            </div>
          </section>

          {/* Comparison Table */}
          <section>
            <h3 className='text-xl font-semibold text-gray-800 mb-3 flex items-center'>
              🏦 {t('wallets.infoModal.differenceTitle')}
            </h3>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm border-collapse'>
                <thead>
                  <tr className='bg-gray-100'>
                    <th className='border border-gray-300 p-3 text-left'></th>
                    <th className='border border-gray-300 p-3 text-center bg-blue-100'>Crypto Wallets Web3</th>
                    <th className='border border-gray-300 p-3 text-center bg-orange-100'>Wallets Custodiadas</th>
                  </tr>
                </thead>
                <tbody className='text-gray-700'>
                  <tr>
                    <td className='border border-gray-300 p-3 font-semibold'>{t('wallets.infoModal.diffControlKeys')}</td>
                    <td className='border border-gray-300 p-3 text-center'>{t('wallets.infoModal.diffControlWeb3')}</td>
                    <td className='border border-gray-300 p-3 text-center'>{t('wallets.infoModal.diffControlCustodial')}</td>
                  </tr>
                  <tr>
                    <td className='border border-gray-300 p-3 font-semibold'>{t('wallets.infoModal.diffResponsibility')}</td>
                    <td className='border border-gray-300 p-3 text-center'>{t('wallets.infoModal.diffResponsibilityWeb3')}</td>
                    <td className='border border-gray-300 p-3 text-center'>{t('wallets.infoModal.diffResponsibilityCustodial')}</td>
                  </tr>
                  <tr>
                    <td className='border border-gray-300 p-3 font-semibold'>{t('wallets.infoModal.diffRecovery')}</td>
                    <td className='border border-gray-300 p-3 text-center'>{t('wallets.infoModal.diffRecoveryWeb3')}</td>
                    <td className='border border-gray-300 p-3 text-center'>{t('wallets.infoModal.diffRecoveryCustodial')}</td>
                  </tr>
                  <tr>
                    <td className='border border-gray-300 p-3 font-semibold'>{t('wallets.infoModal.diffFlexibility')}</td>
                    <td className='border border-gray-300 p-3 text-center'>{t('wallets.infoModal.diffFlexibilityWeb3')}</td>
                    <td className='border border-gray-300 p-3 text-center'>{t('wallets.infoModal.diffFlexibilityCustodial')}</td>
                  </tr>
                  <tr>
                    <td className='border border-gray-300 p-3 font-semibold'>{t('wallets.infoModal.diffGasFees')}</td>
                    <td className='border border-gray-300 p-3 text-center'>{t('wallets.infoModal.diffGasFeesWeb3')}</td>
                    <td className='border border-gray-300 p-3 text-center'>{t('wallets.infoModal.diffGasFeesCustodial')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Warnings Section */}
          <section>
            <h3 className='text-xl font-semibold text-gray-800 mb-3 flex items-center'>
              ⚠️ {t('wallets.infoModal.warningsTitle')}
            </h3>

            <div className='space-y-3'>
              <div className='bg-green-50 border-l-4 border-green-500 p-4'>
                <p className='font-semibold text-green-800 mb-2'>✅ {t('wallets.infoModal.warningsAlways')}</p>
                <ul className='space-y-1 text-sm text-green-900'>
                  <li>• {t('wallets.infoModal.warningsNetwork')}</li>
                  <li>• {t('wallets.infoModal.warningsGas')}</li>
                  <li>• {t('wallets.infoModal.warningsAddress')}</li>
                </ul>
              </div>

              <div className='bg-red-50 border-l-4 border-red-500 p-4'>
                <p className='font-semibold text-red-800 mb-2'>❌ {t('wallets.infoModal.warningsNever')}</p>
                <ul className='space-y-1 text-sm text-red-900'>
                  <li>• {t('wallets.infoModal.warningsShare')}</li>
                  <li>• {t('wallets.infoModal.warningsSend')}</li>
                  <li>• {t('wallets.infoModal.warningsLose')}</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Help Section */}
          <section className='bg-blue-50 p-4 rounded-lg'>
            <h3 className='text-lg font-semibold text-blue-800 mb-2 flex items-center'>
              🆘 {t('wallets.infoModal.helpTitle')}
            </h3>
            <p className='text-sm text-blue-900'>{t('wallets.infoModal.helpDescription')}</p>
          </section>
        </div>

        {/* Footer */}
        <div className='sticky bottom-0 bg-white border-t border-gray-200 p-4 flex justify-center rounded-b-[10px]'>
          <button
            onClick={onClose}
            className="dashboard-btn dashboard-btn-default px-8"
          >
            {t('wallets.infoModal.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
